import { C } from '../../styles/tokens';
import GenerationsTableSkeleton from '../skeletons/GenerationsTableSkeleton';
import useIsMobile from '../../hooks/useIsMobile';

// ── Shared "Generations" shell — used by Dubbing / Voice Changer / Voice Isolator ──
// Purely presentational: header (title + count badge), column-header row, card
// wrapper, loading/empty states. Each page supplies its own per-row component
// (with its own hooks for play/pause/download/delete state) via `renderRow`,
// so real data-fetching/mutation logic lives in each page, not here.

function EmptyState({ icon, title, subtitle }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 24px', gap: 8 }}>
            <div style={{
                width: 56, height: 56, borderRadius: '50%', background: C.n100,
                display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 4,
            }}>
                {icon}
            </div>
            <p style={{ fontSize: 15, fontWeight: 700, color: C.n700, margin: 0 }}>{title}</p>
            {subtitle && (
                <p style={{ fontSize: 13, color: C.n400, margin: 0, textAlign: 'center', maxWidth: 320 }}>
                    {subtitle}
                </p>
            )}
        </div>
    );
}

export default function GenerationsTable({
    title = 'Generations',
    count,
    columns,
    items,
    rowKey,
    renderRow,
    emptyIcon,
    emptyTitle = 'Nothing here yet',
    emptySubtitle,
    loading,
    actionsWidth = '70px',
}) {
    const isMobile = useIsMobile();

    // Fixed width (not 'auto') so the header row and every data row compute IDENTICAL grid
    // tracks — the header's actions cell is always empty (no buttons), so an 'auto' track
    // would size to 0 there but wider in each data row, shifting every column out of
    // alignment between the header and the rows.
    const gridTemplateColumns = ['1fr', ...columns.map(c => c.width || '90px'), actionsWidth].join(' ');

    return (
        <div style={{
            marginTop: 32, background: C.w, border: `1px solid ${C.n200}`, borderRadius: 16,
            boxShadow: '0 1px 3px rgba(15,23,42,.04)', overflow: 'hidden',
        }}>
            {/* Header */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '14px 20px', borderBottom: `1px solid ${C.n200}`,
            }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: C.n900 }}>{title}</span>
                {typeof count === 'number' && (
                    <span style={{
                        padding: '2px 8px', borderRadius: 999, background: C.n100,
                        color: C.n500, fontSize: 12, fontWeight: 600,
                    }}>
                        {count}
                    </span>
                )}
            </div>

            {loading ? (
                <GenerationsTableSkeleton gridTemplateColumns={gridTemplateColumns} isMobile={isMobile} />
            ) : !items.length ? (
                <EmptyState icon={emptyIcon} title={emptyTitle} subtitle={emptySubtitle} />
            ) : (
                <div>
                    {/* Column header row — meaningless once rows stack on mobile, so hidden there */}
                    {!isMobile && (
                        <div style={{
                            display: 'grid', gridTemplateColumns, gap: 12,
                            padding: '10px 20px', background: C.n50,
                            fontSize: 11.5, fontWeight: 600, color: C.n400,
                            textTransform: 'uppercase', letterSpacing: '.03em',
                        }}>
                            <span>Name</span>
                            {columns.map(col => <span key={col.key} style={{ textAlign: 'center' }}>{col.label}</span>)}
                            <span />
                        </div>
                    )}
                    <div>
                        {items.map(item => renderRow(item, gridTemplateColumns, rowKey(item), isMobile))}
                    </div>
                </div>
            )}
        </div>
    );
}
